import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { cn } from '../lib/utils';

const STORAGE_KEY = 'gappaq-admin-theme';

const AdminThemeContext = createContext(null);

const readStoredTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
};

export const AdminThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);
  const isDark = theme === 'dark';

  const setTheme = useCallback((next) => {
    const value = next === 'light' ? 'light' : 'dark';
    setThemeState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(isDark ? 'light' : 'dark');
  }, [isDark, setTheme]);

  const value = useMemo(
    () => ({ theme, isDark, setTheme, toggleTheme }),
    [theme, isDark, setTheme, toggleTheme]
  );

  return (
    <AdminThemeContext.Provider value={value}>
      <div
        className={cn(
          'admin-shell min-h-screen bg-background text-foreground',
          isDark && 'dark'
        )}
      >
        {children}
      </div>
    </AdminThemeContext.Provider>
  );
};

export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within AdminThemeProvider');
  }
  return context;
};
